/**
 * #1 一鍵加入(2026-09-24 取代入會表單 + 驗證碼)
 *
 * 只有一顆「同意條款並加入」。姓名 / 手機不在這裡收 —— LINE 根本不給手機,
 * 真正需要的是結帳 / 送禮,那時再補(/complete-profile)。推薦碼選填、預設收合。
 *
 * 進入方式:
 *   - Splash 對非 active 會員導過來;帶 ?next= 的話加入後直接去那頁
 *   - GiftReceive 非會員選「成為會員」也導這裡,next 回禮物頁接著領
 */
import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { checkReferralCode, joinMember } from "../api/members";
import { useAuthStore } from "../store/authStore";
import { useMemberStore } from "../store/memberStore";
import { useUIStore } from "../store/uiStore";
import { nextFromSearch } from "../utils/join";

const PERKS: { text: string }[] = [
  { text: "燕窩存摺 — 視覺化剩餘組數" },
  { text: "溯源憑證 — 每批燕窩可查" },
  { text: "電子送禮 — 為家人添一份心意" },
  { text: "私宴預約 — 品鑑會與活動邀請" },
];

export default function Join() {
  const navigate = useNavigate();
  const location = useLocation();
  const jwt = useAuthStore((s) => s.jwt);
  const displayName = useMemberStore((s) => s.member?.display_name) || "";
  const next = nextFromSearch(location.search, "/home");

  // Auth guard:LIFF SDK 透過 liff.state 直接帶 user 到 /join 時 Splash 不會跑,
  // authStore 沒 JWT → 提交時 401。沒 JWT 就先回 / 讓 Splash 做 liff-verify + 路由決策。
  useEffect(() => {
    if (!jwt) navigate("/", { replace: true });
  }, [jwt, navigate]);

  const [consent, setConsent] = useState(true);
  const [showReferral, setShowReferral] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  // 推薦碼即時查詢狀態:null=未查 / checking / 查到(valid + label)
  const [referralInfo, setReferralInfo] = useState<
    { checking: boolean; valid: boolean; label: string } | null
  >(null);
  const [busy, setBusy] = useState(false);

  // 推薦碼即時查詢 — debounce 400ms,讓客戶填碼當下就看到「推薦人:王顧問」確認沒填錯人。
  // 選填,查不到也不擋加入。
  useEffect(() => {
    const raw = referralCode.trim();
    if (!raw) {
      setReferralInfo(null);
      return;
    }
    setReferralInfo({ checking: true, valid: false, label: "" });
    const t = setTimeout(async () => {
      try {
        const r = await checkReferralCode(raw);
        setReferralInfo({ checking: false, valid: r.valid, label: r.label });
      } catch {
        setReferralInfo(null); // 查詢失敗不顯示狀態,不打斷加入
      }
    }, 400);
    return () => clearTimeout(t);
  }, [referralCode]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!consent) {
      useUIStore.getState().showToast("請先勾選同意條款", "error");
      return;
    }
    setBusy(true);
    try {
      const r = await joinMember({ consent: true, referral_code: referralCode.trim() || undefined });
      if (r.status === "ok" || r.status === "already_active") {
        if (r.member) useMemberStore.getState().setMember(r.member);
        if (r.status === "ok") useUIStore.getState().showToast("歡迎加入霽雲", "success");
        navigate(next, { replace: true });
        return;
      }
      if (r.status === "suspended") {
        // 後台停用中 — 回 Splash,那裡會顯示「會員資格暫停」畫面
        navigate("/", { replace: true });
        return;
      }
      useUIStore.getState().showToast(
        r.status === "consent_required" ? "請先勾選同意條款" : "加入未完成,請稍候再試",
        "error",
      );
    } catch {
      // network / 5xx — 不曝機器碼,給 user 雅緻提示
      useUIStore.getState().showToast("連線異常,請稍候再試", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-8">
        <div className="text-sm opacity-80">霽雲會員</div>
        <h1 className="text-2xl font-bold mt-1">
          {displayName ? `${displayName},歡迎光臨` : "歡迎光臨"}
        </h1>
        <p className="text-sm mt-1 opacity-90">一鍵成為會員,不必填寫任何資料</p>
      </header>

      <form onSubmit={submit} className="px-5 mt-5 space-y-4">
        <div className="card">
          <div className="text-sm text-muted mb-3 font-medium">加入即可享有</div>
          <ul className="space-y-2 text-sm text-ink">
            {PERKS.map((p) => (
              <li key={p.text} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
                <span className="leading-6">{p.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 推薦碼 — 選填、預設收合,不想填的人一顆按鈕就走 */}
        {!showReferral ? (
          <button
            type="button"
            onClick={() => setShowReferral(true)}
            className="text-sm text-primary underline underline-offset-4"
          >
            有推薦碼?點此輸入
          </button>
        ) : (
          <div>
            <label className="label" htmlFor="join-referral">推薦碼(選填)</label>
            <input
              id="join-referral"
              aria-label="推薦碼(選填)"
              className="input uppercase"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="若有專屬顧問 / 推薦人,請輸入其推薦碼"
              maxLength={20}
              autoFocus
            />
            {referralInfo?.checking && (
              <p className="text-xs text-muted mt-1">查詢中…</p>
            )}
            {referralInfo && !referralInfo.checking && referralInfo.valid && (
              <p className="text-xs text-sage mt-1">
                <Check size={14} strokeWidth={2} className="inline -mt-0.5 mr-1" aria-hidden="true" />
                推薦人:{referralInfo.label || "已確認"}
              </p>
            )}
            {referralInfo && !referralInfo.checking && !referralInfo.valid && (
              <p className="text-xs text-muted mt-1">查無此推薦碼,可留空或向您的顧問確認</p>
            )}
          </div>
        )}

        <label className="flex items-start gap-3 mt-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1"
          />
          <span>
            我同意霽雲依個資法處理我的 LINE 帳號資料,用於會員服務與相關通知;
            日後結帳或送禮時再提供姓名與手機。資料 100% 歸我所有,可隨時要求刪除。
          </span>
        </label>

        <button type="submit" disabled={busy || !consent} className="btn-primary w-full mt-2">
          {busy ? "加入中…" : "同意條款並加入"}
        </button>
        <p className="text-xs text-muted text-center">
          結帳或送禮時,才需要補姓名與手機
        </p>
      </form>
    </div>
  );
}
